import { cn } from "@/utils";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check, ChevronDown, Search, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { FieldShell } from "./Field";
import {
  alturaDaListaVisivel,
  medirEspacoDoPopover,
  semAcento,
} from "./popover.position";

/**
 * Select do Design System — a lista é React, não o seletor do sistema.
 *
 * O `<select>` nativo abre a roda do iOS, o diálogo do Android e um menu
 * diferente em cada navegador de desktop. São três aparências para o mesmo
 * campo, e nenhuma delas é a Fidexa. Aqui a lista é desenhada por nós, então o
 * lojista vê exatamente a mesma coisa no balcão, no celular e no escritório.
 *
 * O que se perde ao abrir mão do nativo é tudo que ele dava de graça: teclado,
 * leitor de tela e o alvo de toque confortável. Por isso este componente
 * implementa o padrão ARIA de combobox por inteiro:
 *
 * - navegação por setas, Home, End, Enter, Espaço e Esc;
 * - busca por digitação (digite "war" e vai para Warning);
 * - `aria-activedescendant`, para o foco nunca sair do gatilho;
 * - opções com 48px de altura.
 *
 * Em telas pequenas a lista não vira um menuzinho: vira folha de baixo, com
 * área segura, que é o gesto que a mão já conhece no celular.
 */

export type SelectOption = {
  value: string;
  label: string;
  /** Segunda linha da opção. Use para desambiguar, não para texto longo. */
  description?: string;
  disabled?: boolean;
};

export interface SelectProps {
  label: string;
  options: SelectOption[];
  /** Controlado. Para não-controlado, use `defaultValue`. */
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  /** Nome do campo num formulário HTML comum (gera um input oculto). */
  name?: string;
  /**
   * Clicar na opção já escolhida limpa a escolha. Ligado por padrão em campo
   * opcional e desligado em campo obrigatório, onde "sem valor" não é resposta
   * válida.
   */
  allowDeselect?: boolean;
  containerClassName?: string;
  className?: string;
}

const proximoHabilitado = (
  options: SelectOption[],
  inicio: number,
  passo: 1 | -1
): number => {
  const total = options.length;
  for (let i = 1; i <= total; i += 1) {
    const indice = (inicio + passo * i + total * total) % total;
    if (!options[indice].disabled) return indice;
  }
  return inicio;
};

/**
 * A partir de quantas opções a lista ganha campo de busca. Com cinco ou menos,
 * o olho varre mais rápido do que a mão digita.
 */
const LIMIAR_DE_BUSCA = 5;

/** Espaço do campo de busca, quando existe, mais a borda de baixo dele. */
const ALTURA_DA_BUSCA = 60;

const primeiroHabilitado = (options: SelectOption[]): number => {
  const indice = options.findIndex(option => !option.disabled);
  return indice === -1 ? 0 : indice;
};

export const Select = ({
  label,
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = "Selecione",
  hint,
  error,
  required,
  disabled,
  hideLabel,
  name,
  allowDeselect,
  containerClassName,
  className,
}: SelectProps) => {
  const idBase = useId();
  const triggerId = `${idBase}-trigger`;
  const listId = `${idBase}-list`;
  const hintId = `${idBase}-hint`;
  const errorId = `${idBase}-error`;

  const [aberto, setAberto] = useState(false);
  const [valorInterno, setValorInterno] = useState(defaultValue ?? "");
  const [ativo, setAtivo] = useState(-1);
  const [busca, setBusca] = useState("");

  // Abaixo de 640px a lista é folha de baixo e não precisa de cálculo nenhum.
  // Daqui para cima ela é ancorada no campo, e aí sim precisa saber se cabe.
  const [ancorada, setAncorada] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 40rem)").matches
  );
  const [posicao, setPosicao] = useState({ paraCima: false, alturaMax: 256 });

  const wrapperRef = useRef<HTMLDivElement>(null);
  const painelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const opcoesRef = useRef<(HTMLDivElement | null)[]>([]);
  const campoDeBuscaRef = useRef<HTMLInputElement>(null);
  const buscaRef = useRef({ termo: "", ate: 0 });

  const controlado = value !== undefined;
  const valor = controlado ? value : valorInterno;
  const selecionado = options.find(option => option.value === valor);
  const comBusca = options.length > LIMIAR_DE_BUSCA;
  const podeLimpar = allowDeselect ?? !required;

  /**
   * A opção escolhida sempre em primeiro. Numa lista de trinta lojas, conferir
   * o que está valendo custava rolar até achar o "certo" perdido no meio.
   *
   * A ordem é congelada enquanto a lista está aberta (`aberto` é a dependência):
   * reordenar a cada escolha faria as opções pularem sob o cursor.
   */
  const opcoesOrdenadas = useMemo(() => {
    const escolhida = options.find(option => option.value === valor);
    if (!escolhida) return options;
    return [escolhida, ...options.filter(option => option !== escolhida)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, aberto]);

  const opcoesVisiveis = useMemo(() => {
    if (!comBusca || busca.trim() === "") return opcoesOrdenadas;
    const termo = semAcento(busca.trim());
    return opcoesOrdenadas.filter(option =>
      semAcento(`${option.label} ${option.description ?? ""}`).includes(termo)
    );
  }, [opcoesOrdenadas, busca, comBusca]);
  const semMovimento = useReducedMotion();
  const duracao = (ms: number) => (semMovimento ? 0 : ms / 1000);

  const selecionar = useCallback(
    (option: SelectOption) => {
      if (option.disabled) return;

      // Clicar no que já está escolhido desmarca: é o gesto que o usuário tenta
      // quando quer "voltar ao normal" e não encontra um botão de limpar.
      const novo = podeLimpar && option.value === valor ? "" : option.value;

      if (!controlado) setValorInterno(novo);
      onValueChange?.(novo);
      setAberto(false);
      triggerRef.current?.focus();
    },
    [controlado, onValueChange, podeLimpar, valor]
  );

  /** Ver `popover.position.ts`: a conta é a mesma do `Combobox`. */
  const medirEspaco = useCallback(() => {
    const gatilho = triggerRef.current;
    if (!gatilho) return;
    setPosicao(medirEspacoDoPopover(gatilho));
  }, []);

  const abrir = useCallback(() => {
    if (disabled) return;
    // Busca de ontem reaparecendo com a lista filtrada faria o usuário achar
    // que sumiram opções.
    setBusca("");
    // A escolhida vai para o topo, então ela é sempre o índice 0 quando existe.
    setAtivo(selecionado ? 0 : primeiroHabilitado(options));
    // Mede ANTES de abrir: se a lista já aparecesse para baixo e só depois
    // pulasse para cima, o usuário veria o salto.
    medirEspaco();
    setAberto(true);
  }, [disabled, medirEspaco, options, selecionado]);

  // Com campo de busca, o foco vai para ele: é o que permite digitar sem um
  // segundo clique. Sem busca, o foco fica no gatilho e a lista é conduzida por
  // `aria-activedescendant`.
  useEffect(() => {
    if (aberto && comBusca) campoDeBuscaRef.current?.focus();
  }, [aberto, comBusca]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 40rem)");
    const aoMudar = () => setAncorada(mq.matches);
    mq.addEventListener("change", aoMudar);
    return () => mq.removeEventListener("change", aoMudar);
  }, []);

  // Clique fora fecha.
  useEffect(() => {
    if (!aberto) return;
    const foraDaLista = (event: MouseEvent) => {
      const alvo = event.target as Node;
      // O painel checa separado porque, em folha de baixo, ele é levado para o
      // `body` por portal e deixa de estar dentro do wrapper.
      if (wrapperRef.current?.contains(alvo)) return;
      if (painelRef.current?.contains(alvo)) return;
      setAberto(false);
    };
    document.addEventListener("mousedown", foraDaLista);
    return () => document.removeEventListener("mousedown", foraDaLista);
  }, [aberto]);

  // Rolar ou redimensionar muda o espaço disponível, então a lista recalcula de
  // que lado abre. `capture` é necessário para pegar a rolagem de containers
  // internos, não só a da janela.
  useEffect(() => {
    if (!aberto || !ancorada) return;
    const recalcular = () => medirEspaco();
    window.addEventListener("scroll", recalcular, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", recalcular);
    return () => {
      window.removeEventListener("scroll", recalcular, { capture: true });
      window.removeEventListener("resize", recalcular);
    };
  }, [aberto, ancorada, medirEspaco]);

  // Mantém a opção ativa visível ao navegar por teclado.
  useEffect(() => {
    if (!aberto || ativo < 0) return;
    opcoesRef.current[ativo]?.scrollIntoView({ block: "nearest" });
  }, [aberto, ativo]);

  const aoDigitar = (event: KeyboardEvent<HTMLButtonElement>) => {
    const { key } = event;

    if (!aberto) {
      if (
        key === "ArrowDown" ||
        key === "ArrowUp" ||
        key === "Enter" ||
        key === " "
      ) {
        event.preventDefault();
        abrir();
      }
      return;
    }

    if (key === "Escape" || key === "Tab") {
      setAberto(false);
      return;
    }

    if (key === "ArrowDown") {
      event.preventDefault();
      setAtivo(atual => proximoHabilitado(opcoesVisiveis, atual, 1));
      return;
    }

    if (key === "ArrowUp") {
      event.preventDefault();
      setAtivo(atual => proximoHabilitado(opcoesVisiveis, atual, -1));
      return;
    }

    if (key === "Home") {
      event.preventDefault();
      setAtivo(primeiroHabilitado(opcoesVisiveis));
      return;
    }

    if (key === "End") {
      event.preventDefault();
      setAtivo(proximoHabilitado(opcoesVisiveis, 0, -1));
      return;
    }

    if (key === "Enter" || key === " ") {
      event.preventDefault();
      if (opcoesVisiveis[ativo]) selecionar(opcoesVisiveis[ativo]);
      return;
    }

    // Busca por digitação: o teclado é o caminho mais rápido em lista conhecida.
    // Com campo de busca, quem cuida das letras é ele.
    if (
      !comBusca &&
      key.length === 1 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey
    ) {
      const agora = Date.now();
      const termo =
        agora > buscaRef.current.ate
          ? key.toLowerCase()
          : buscaRef.current.termo + key.toLowerCase();
      buscaRef.current = { termo, ate: agora + 600 };
      const encontrado = opcoesVisiveis.findIndex(
        option =>
          !option.disabled && option.label.toLowerCase().startsWith(termo)
      );
      if (encontrado >= 0) setAtivo(encontrado);
    }
  };

  const alturaDaLista = alturaDaListaVisivel(
    posicao.alturaMax,
    comBusca ? ALTURA_DA_BUSCA : 0
  );

  /**
   * Dentro do campo de busca, as setas continuam conduzindo a lista e Enter
   * escolhe — o foco nunca sai do campo, senão cada tecla exigiria voltar para
   * ele.
   */
  const aoTeclarNaBusca = (event: KeyboardEvent<HTMLInputElement>) => {
    const { key } = event;

    if (key === "Escape") {
      event.stopPropagation();
      setAberto(false);
      triggerRef.current?.focus();
      return;
    }

    if (key === "Tab") {
      setAberto(false);
      return;
    }

    if (key === "ArrowDown") {
      event.preventDefault();
      setAtivo(atual => proximoHabilitado(opcoesVisiveis, atual, 1));
      return;
    }

    if (key === "ArrowUp") {
      event.preventDefault();
      setAtivo(atual => proximoHabilitado(opcoesVisiveis, atual, -1));
      return;
    }

    if (key === "Enter") {
      event.preventDefault();
      if (opcoesVisiveis[ativo]) selecionar(opcoesVisiveis[ativo]);
    }
  };

  return (
    <FieldShell
      label={label}
      hideLabel={hideLabel}
      hint={hint}
      error={error}
      required={required}
      controlId={triggerId}
      hintId={hintId}
      errorId={errorId}
      className={containerClassName}
    >
      <div ref={wrapperRef} className="relative">
        {name ? <input type="hidden" name={name} value={valor} /> : null}

        <button
          ref={triggerRef}
          id={triggerId}
          type="button"
          // Com campo de busca, quem vira o combobox é o input: dois elementos
          // declarando `aria-activedescendant` fazem o leitor de tela não
          // anunciar opção nenhuma. Aqui o gatilho volta a ser só o botão que
          // abre a lista.
          role={comBusca ? undefined : "combobox"}
          aria-haspopup="listbox"
          aria-expanded={aberto}
          // Marca lida pelo `Modal`: com a lista aberta, o Esc fecha a lista e
          // não o modal inteiro.
          data-popover-aberto={aberto || undefined}
          aria-controls={aberto && !comBusca ? listId : undefined}
          aria-activedescendant={
            !comBusca && aberto && ativo >= 0
              ? `${idBase}-opt-${ativo}`
              : undefined
          }
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : hint ? hintId : undefined}
          disabled={disabled}
          onClick={() => (aberto ? setAberto(false) : abrir())}
          onKeyDown={aoDigitar}
          className={cn(
            "flex min-h-12 w-full cursor-pointer items-center justify-between gap-2 px-3 text-left",
            "font-ds-sans text-ds-body text-ds-ink-900",
            "rounded-ds-md border-ds-ink-500 bg-ds-surface border",
            "ease-ds-out transition-[border-color,box-shadow] duration-150",
            "hover:border-ds-ink-600",
            "focus-visible:border-ds-brand-500 focus-visible:ring-ds-focus focus-visible:ring-2 focus-visible:outline-none",
            "disabled:bg-ds-ink-100 disabled:text-ds-ink-600 disabled:hover:border-ds-ink-500 disabled:cursor-not-allowed",
            "aria-[invalid=true]:border-ds-danger-700",
            "motion-reduce:transition-none",
            className
          )}
        >
          <span className={cn("truncate", !selecionado && "text-ds-ink-600")}>
            {selecionado?.label ?? placeholder}
          </span>
          <ChevronDown
            aria-hidden="true"
            className={cn(
              "text-ds-ink-600 ease-ds-out size-5 shrink-0 transition-transform duration-150 motion-reduce:transition-none",
              aberto && "rotate-180"
            )}
          />
        </button>

        <AnimatePresence>
          {aberto ? (
            // `motion` com `key` como filho direto: com um Fragment aqui, o
            // framer não sabe quando a saída terminou e deixa o popover
            // pendurado no DOM, invisível mas ainda no leitor de tela.
            <motion.div key="popover">
              {/* Fundo só no celular: acompanha a folha de baixo. */}
              {/* O véu FECHA ao toque. Sem isso — e sem Esc, que não existe no
                  celular — a única saída da folha era escolher uma opção: o
                  usuário ficava preso numa escolha que só queria espiar. */}
              <motion.div
                key="veu"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: duracao(180) }}
                onClick={() => setAberto(false)}
                className="bg-ds-scrim/40 fixed inset-0 z-40 sm:hidden"
              />

              <motion.div
                key="painel"
                ref={painelRef}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 12 }}
                transition={{
                  duration: duracao(180),
                  ease: [0.16, 1, 0.3, 1],
                }}
                // A altura medida limita o PAINEL, não a lista de dentro: no
                // painel ela recorta o branco junto com as opções. Na lista,
                // como estava, as opções passavam do fundo branco e caíam por
                // cima dos campos de baixo.
                style={
                  ancorada
                    ? {
                        top: posicao.paraCima ? "auto" : "calc(100% + 4px)",
                        bottom: posicao.paraCima ? "calc(100% + 4px)" : "auto",
                        maxHeight: posicao.alturaMax,
                      }
                    : undefined
                }
                className={cn(
                  // Celular: folha de baixo, com área segura.
                  "rounded-t-ds-xl border-ds-border fixed inset-x-0 bottom-0 z-50 flex max-h-[60vh] flex-col border-t",
                  "bg-ds-surface shadow-ds-sheet pb-[env(safe-area-inset-bottom)]",
                  // Desktop: lista ancorada no campo.
                  //
                  // A largura parte da do campo e **cresce até caber a maior
                  // opção**: presa à do campo (`inset-x-0`), a lista de um
                  // gatilho estreito — o seletor de escopo da busca, que mede
                  // o texto "Empresa" — cortava "Identificador" no meio. O teto
                  // impede que ela vaze a janela num campo encostado na borda.
                  "sm:absolute sm:inset-x-auto sm:left-0 sm:w-max sm:max-w-[min(20rem,calc(100vw-2rem))] sm:min-w-full",
                  "sm:top-[calc(100%+4px)] sm:bottom-auto sm:max-h-64",
                  "sm:rounded-ds-md sm:shadow-ds-overlay sm:border"
                )}
              >
                <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-2 sm:hidden">
                  <span className="font-ds-sans text-ds-small text-ds-ink-900 font-semibold">
                    {label}
                  </span>
                  {/* Gesto e véu são atalhos; o botão de fechar está sempre
                      presente, como no Modal. */}
                  <button
                    type="button"
                    onClick={() => setAberto(false)}
                    aria-label="Fechar lista"
                    className="rounded-ds-sm text-ds-ink-700 hover:bg-ds-ink-100 focus-visible:ring-ds-focus focus-visible:ring-offset-ds-surface -mr-2 inline-flex size-11 shrink-0 cursor-pointer items-center justify-center focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                  >
                    <X aria-hidden="true" className="size-5" />
                  </button>
                </div>

                {comBusca ? (
                  <div className="border-ds-border shrink-0 border-b p-2">
                    <div className="relative flex items-center">
                      <Search
                        aria-hidden="true"
                        className="text-ds-ink-500 pointer-events-none absolute left-3 size-4"
                      />
                      <input
                        ref={campoDeBuscaRef}
                        type="text"
                        role="combobox"
                        aria-expanded={true}
                        aria-autocomplete="list"
                        value={busca}
                        onChange={evento => {
                          setBusca(evento.target.value);
                          // Cada letra muda a lista: o ativo volta para o
                          // primeiro resultado, senão Enter escolheria uma
                          // opção que não está mais na tela.
                          setAtivo(0);
                        }}
                        onKeyDown={aoTeclarNaBusca}
                        aria-controls={listId}
                        aria-activedescendant={
                          ativo >= 0 ? `${idBase}-opt-${ativo}` : undefined
                        }
                        aria-label={`Buscar em ${label}`}
                        placeholder="Buscar…"
                        className={cn(
                          "font-ds-sans text-ds-body text-ds-ink-900 placeholder:text-ds-ink-600",
                          "rounded-ds-md border-ds-border bg-ds-surface min-h-11 w-full border py-2 pr-3 pl-9",
                          "focus:border-ds-brand-500 focus:ring-ds-focus focus:ring-2 focus:outline-none"
                        )}
                      />
                    </div>
                  </div>
                ) : null}

                <div
                  id={listId}
                  role="listbox"
                  aria-label={label}
                  style={ancorada ? { maxHeight: alturaDaLista } : undefined}
                  className="min-h-0 flex-1 overflow-y-auto p-1"
                >
                  {opcoesVisiveis.map((option, indice) => {
                    const estaSelecionado = option.value === valor;
                    const estaAtivo = indice === ativo;
                    return (
                      <div
                        key={option.value}
                        ref={elemento => {
                          opcoesRef.current[indice] = elemento;
                        }}
                        id={`${idBase}-opt-${indice}`}
                        role="option"
                        aria-selected={estaSelecionado}
                        aria-disabled={option.disabled || undefined}
                        onClick={() => selecionar(option)}
                        onMouseEnter={() =>
                          !option.disabled && setAtivo(indice)
                        }
                        className={cn(
                          "rounded-ds-sm flex min-h-12 items-center justify-between gap-3 px-3 py-2",
                          "font-ds-sans text-ds-body",
                          option.disabled
                            ? "text-ds-ink-600 cursor-not-allowed opacity-60"
                            : "text-ds-ink-900 cursor-pointer",
                          estaAtivo && !option.disabled && "bg-ds-brand-50",
                          estaSelecionado && "font-medium"
                        )}
                      >
                        <span className="flex flex-col">
                          <span>{option.label}</span>
                          {option.description ? (
                            <span className="font-ds-sans text-ds-small text-ds-ink-600">
                              {option.description}
                            </span>
                          ) : null}
                        </span>
                        {/* Selecionado tem o check além do peso: cor e negrito
                            sozinhos não comunicam estado (WCAG 1.4.1). */}
                        {estaSelecionado ? (
                          <Check
                            aria-hidden="true"
                            className="text-ds-brand-700 size-5 shrink-0"
                          />
                        ) : null}
                      </div>
                    );
                  })}

                  {opcoesVisiveis.length === 0 ? (
                    <p className="font-ds-sans text-ds-small text-ds-ink-600 px-3 py-6 text-center">
                      Nenhuma opção encontrada.
                    </p>
                  ) : null}
                </div>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </FieldShell>
  );
};
